/**
 * Escalera de severidad compartida (D-10/D-11 de `31.3-05-PLAN.md`).
 *
 * Origen literal: `designated-routes-accordeon.component.ts:333-420`, movido
 * verbatim a funciones puras exportadas para que `31.6-06` (badge de cada
 * ruta rapida de la lista de hoy) pueda consumirlas sin arrastrar el
 * componente del acordeon (plan `31.6-04`). Ninguna de las tres ramas del
 * `switch` original cambio de orden ni de valor devuelto -- esa escalera
 * esta firmada por el usuario en la Phase 31.3 y esta fase no la re-litiga.
 *
 * Nota de dominio para D-12 (`31.6-CONTEXT.md`): el estado de
 * geocodificacion (`'valid' | 'missing'`, derivado de `latitude`/
 * `longitude`) NO pasa por estas funciones -- es un dominio de 2 valores
 * fuera del `switch` de `RouteStatus` y se consume directamente con
 * `.severity-success`/`.severity-warning` en el template.
 */

/**
 * Nivel de severidad de la escalera firmada (D-10/D-11 de `31.3-05-PLAN.md`)
 * para el estado de una ruta. Las cuatro filas marcadas **FIRMADO** por el
 * usuario en el discuss-phase no se reabren en el UAT de cierre: los dos
 * estados de cancelacion son final-negativo (critico), y pausa por
 * inactividad/atraso son cautelar (advertencia) aunque comparten el mismo
 * par de tokens que critico -- la distincion es por peso de relleno, no
 * por matiz (D-12), porque no existe ningun token de advertencia en el
 * sistema.
 *
 * `not-completed` es el estado de gestion que el backend envia para un punto
 * no completado y se trata como `incomplete` (A-06 de la Phase 34: icono +
 * texto de advertencia, nunca solo color).
 * @param status - el valor de estado crudo de la ruta
 * @returns el nivel de severidad, o `'unknown'` si el estado no esta en la tabla firmada (nunca `'critical'` por accidente)
 */
export function severityTierFor(
  status: string
): 'critical' | 'warning' | 'success' | 'info' | 'unknown' {
  switch (status) {
    case 'cancelled':
    case 'cancelled-inactivity':
    case 'rejected':
      return 'critical';
    case 'paused-inactivity':
    case 'late':
    case 'incomplete':
    case 'not-completed':
      return 'warning';
    case 'completed':
      return 'success';
    case 'pending':
    case 'in-progress':
    case 'starting-soon':
      return 'info';
    default:
      return 'unknown';
  }
}

/**
 * Icono Lucide de la escalera firmada (D-10/D-11) para el estado de una
 * ruta. El selector conmuta por ESTADO, no por nivel: dentro del mismo
 * nivel informativo, pendiente/en-progreso/por-comenzar usan tres iconos
 * distintos (`info`/`loader-circle`/`clock`).
 *
 * `not-completed` es el estado de gestion que el backend envia para un punto
 * no completado y se trata como `incomplete` (A-06 de la Phase 34).
 * @param status - el valor de estado crudo de la ruta
 * @returns el nombre de icono Lucide (`kebab-case`, tal como lo espera el `ngSwitch` del template), o cadena vacia para un estado desconocido
 */
export function severityIconFor(status: string): string {
  switch (status) {
    case 'cancelled':
    case 'cancelled-inactivity':
    case 'rejected':
      return 'triangle-alert';
    case 'paused-inactivity':
    case 'late':
    case 'incomplete':
    case 'not-completed':
      return 'circle-alert';
    case 'completed':
      return 'circle-check';
    case 'pending':
      return 'info';
    case 'in-progress':
      return 'loader-circle';
    case 'starting-soon':
      return 'clock';
    default:
      return '';
  }
}

/**
 * Clave i18n de `CARD-ASSIGNMENT` de la escalera firmada (D-10/D-11) para
 * el estado de una ruta. El estado pendiente resuelve deliberadamente a la
 * clave de "Asignada" -- comportamiento verificado que se preserva, no un
 * bug a corregir (D-10).
 *
 * `not-completed` es el estado de gestion que el backend envia para un punto
 * no completado y se trata como `incomplete` (A-06 de la Phase 34): misma
 * clave, para que el texto acompañe siempre al icono.
 * @param status - el valor de estado crudo de la ruta
 * @returns la clave i18n bajo `CARD-ASSIGNMENT`, o cadena vacia para un estado desconocido
 */
export function severityLabelKeyFor(status: string): string {
  switch (status) {
    case 'cancelled':
      return 'CARD-ASSIGNMENT.CANCELLED';
    case 'cancelled-inactivity':
      return 'CARD-ASSIGNMENT.CANCELLED-INACTIVITY';
    case 'rejected':
      return 'CARD-ASSIGNMENT.REJECTED';
    case 'paused-inactivity':
      return 'CARD-ASSIGNMENT.PAUSED-INACTIVITY';
    case 'late':
      return 'CARD-ASSIGNMENT.LATE';
    case 'incomplete':
    case 'not-completed':
      return 'CARD-ASSIGNMENT.INCOMPLETED';
    case 'completed':
      return 'CARD-ASSIGNMENT.COMPLETED';
    case 'pending':
      return 'CARD-ASSIGNMENT.ASSIGNED';
    case 'in-progress':
      return 'CARD-ASSIGNMENT.IN-PROGRESS';
    case 'starting-soon':
      return 'CARD-ASSIGNMENT.STARTING-SOON';
    default:
      return '';
  }
}
